import { useApiFetch } from '~/composables/useApiFetch';
import type {
  ChangeOrganizationResponse,
  JoinOrgRequest,
  Organization,
  OrganizationsMember,
} from '~/types/organization';

export async function fetchCurrentOrganization(): Promise<OrganizationsMember> {
  const response = await useApiFetch('/organizations/current', {
    method: 'GET',
    requireAuth: true,
  });

  return await response.json();
}

export async function changeOrganizationApi(
  organizationUuid: string
): Promise<ChangeOrganizationResponse> {
  const response = await useApiFetch(`/organizations/change/current?uuid=${organizationUuid}`, {
    method: 'POST',
    requireAuth: true,
  });

  return await response.json();
}

export async function joinOrgApi(organizationUuid: string): Promise<boolean> {
  const requestBody: JoinOrgRequest = {
    organization_uuid: organizationUuid,
  };

  const response = await useApiFetch('/organizations/member/join', {
    method: 'POST',
    requireAuth: true,
    body: JSON.stringify(requestBody),
  });

  return await response.json();
}

export async function createOrgApi(name: string): Promise<OrganizationsMember> {
  const response = await useApiFetch('/organizations', {
    method: 'POST',
    requireAuth: true,
    body: JSON.stringify({ name }),
  });

  return await response.json();
}

export async function inviteMemberApi(email: string): Promise<void> {
  await useApiFetch('/organizations/member/invite', {
    method: 'POST',
    requireAuth: true,
    body: JSON.stringify({ email }),
  });
}

export async function inviteMembersApi(emails: string[]): Promise<void> {
  await useApiFetch('/organizations/member/invite', {
    method: 'POST',
    requireAuth: true,
    body: JSON.stringify({ emails }),
  });
}

export async function getOrganizationsListApi(): Promise<Organization[]> {
  const response = await useApiFetch('/organizations/list', {
    method: 'GET',
    requireAuth: true,
  });

  return await response.json();
}

export async function leaveOrgApi(organizationUuid: string): Promise<ChangeOrganizationResponse> {
  const response = await useApiFetch('/organizations/member/leave', {
    method: 'POST',
    requireAuth: true,
    body: JSON.stringify({ organization_uuid: organizationUuid }),
  });
  return await response.json();
}

export async function getOrganizationMembersApi(): Promise<OrganizationsMember[]> {
  const response = await useApiFetch('/organizations/members', {
    method: 'GET',
    requireAuth: true,
  });
  return await response.json();
}

export async function updateOrganizationNameApi(
  organizationUuid: string,
  name: string
): Promise<void> {
  await useApiFetch('/organizations', {
    method: 'PUT',
    requireAuth: true,
    body: JSON.stringify({ organizations_uuid: organizationUuid, name: name }),
  });
}

export async function deleteMemberApi(params: { uid: number; id: number }): Promise<void> {
  const { uid, id } = params;
  const query = uid && uid !== 0 ? `uid=${uid}` : `id=${id}`;

  await useApiFetch(`/organizations/member/remove?${query}`, {
    method: 'DELETE',
    requireAuth: true,
  });
}

export async function declineOrganizationApi(organizationUuid: string): Promise<boolean> {
  const response = await useApiFetch('/organizations/member/reject', {
    method: 'POST',
    requireAuth: true,
    body: JSON.stringify({ organization_uuid: organizationUuid }),
  });
  return await response.json();
}

export async function acceptOrganizationApi(organizationToken: string): Promise<boolean> {
  const response = await useApiFetch(`/organizations/member?access_token=${organizationToken}`, {
    method: 'GET',
    requireAuth: true,
  });
  return await response.json();
}

/**
 * 邀請方軟刪除邀請
 * @param organizationUuid 組織 UUID
 * @param targetUid 被邀請人的 UID
 */
export async function softDeleteInviteByInviterApi(
  organizationUuid: string,
  targetUid: number
): Promise<void> {
  await useApiFetch('/organizations/member/invite/delete', {
    method: 'POST',
    requireAuth: true,
    body: JSON.stringify({
      organization_uuid: organizationUuid,
      target_uid: targetUid,
    }),
  });
}

/**
 * 被邀請方軟刪除邀請
 * @param organizationUuid 組織 UUID
 */
export async function softDeleteInviteByInviteeApi(organizationUuid: string): Promise<void> {
  await useApiFetch('/organizations/member/invite/dismiss', {
    method: 'POST',
    requireAuth: true,
    body: JSON.stringify({ organization_uuid: organizationUuid }),
  });
}
